/**
 * uploadStore.js — Cross-route background upload tracker.
 *
 * The KB Drawings panel uses `drawingsApi.upload(...)` which can take
 * 30-60 s per file (vision extraction). Users complained that switching
 * tabs mid-upload made it feel like the upload was cancelled. This
 * store keeps the promise alive globally and exposes per-file progress
 * so any component can render a live progress bar. See
 * `<UploadProgressDock/>` in the portal layout for the always-visible UI.
 */
import { create } from 'zustand';

// AbortControllers live outside the store — Zustand can't serialise them
// and re-renders would rebuild them on every setState. Keyed by upload id.
const _controllers = new Map();

export const registerAbort = (id, controller) => {
  if (controller) _controllers.set(id, controller);
};

export const useUploadStore = create((set, get) => ({
  // uploads: { [id]: { id, filename, kbId, kbName, progress, phase, error, startedAt, cancellable } }
  uploads: {},

  start: ({ id, filename, kbId, kbName, cancellable = true }) => set((s) => ({
    uploads: { ...s.uploads, [id]: {
      id, filename, kbId, kbName, progress: 0, phase: 'uploading', error: null,
      startedAt: Date.now(), cancellable,
    } },
  })),

  setProgress: (id, progress) => set((s) => {
    const u = s.uploads[id]; if (!u) return {};
    return { uploads: { ...s.uploads, [id]: { ...u, progress } } };
  }),

  setPhase: (id, phase) => set((s) => {
    const u = s.uploads[id]; if (!u) return {};
    return { uploads: { ...s.uploads, [id]: { ...u, phase } } };
  }),

  finish: (id, { error } = {}) => set((s) => {
    _controllers.delete(id);
    const u = s.uploads[id]; if (!u) return {};
    return { uploads: { ...s.uploads, [id]: { ...u, phase: error ? 'failed' : 'done',
      error: error || null, progress: 100 } } };
  }),

  cancel: (id) => {
    const controller = _controllers.get(id);
    if (controller) {
      try { controller.abort(); } catch (_e) { /* noop */ }
      _controllers.delete(id);
    }
    set((s) => {
      const u = s.uploads[id]; if (!u) return {};
      return { uploads: { ...s.uploads, [id]: { ...u, phase: 'cancelled',
        error: 'Cancelled by user', progress: 100 } } };
    });
  },

  dismiss: (id) => set((s) => {
    _controllers.delete(id);
    const next = { ...s.uploads }; delete next[id]; return { uploads: next };
  }),

  active: () => Object.values(get().uploads).filter((u) => u.phase !== 'done' && u.phase !== 'failed' && u.phase !== 'cancelled'),
}));
